import { TransformerService } from '@nuxtDatakodeUtils/services/transformer.service'
import type { Opportunity } from '~/models/Opportunity'
import { Like } from '~/models/Like'
import { Comment } from '~/models/Comment'

type OpportunityData = PildomModules.Voirie.Data.OpportunityData
type OrganizationData = App.Data.Organization.OrganizationData
type LikeData = PildomModules.Voirie.Data.LikeData
type CommentData = PildomModules.Voirie.Data.CommentData
type IntentionData = PildomModules.Voirie.Data.IntentionData

export class OpportunityService {
    private _opportunity: Opportunity

    constructor(opportunity: Opportunity) {
        this._opportunity = opportunity
        const { $translate, $config, $getColor } = useNuxtApp()
        this.$translate = $translate
        // this.$formatDate = $formatDate
        // this.$formatDateTime = $formatDateTime
        this.$config = $config
        this.$getColor = $getColor
    }

    getIntentions(): IntentionData[] {
        return (this._opportunity.data as OpportunityData).intentions as IntentionData[]
    }

    getOrganizationLike(organization: OrganizationData): LikeData {
        const res = (this._opportunity.data as OpportunityData).likes.find((l) => {
            return l.organization.id === organization.id
        })
        return res || null
    }

    getOrganizationIntentions(organization: OrganizationData): IntentionData[] {
        return this.getIntentions().filter((intention: IntentionData) => {
            return intention.organization.id === organization.id
        })
    }

    getOrganizationLikeChip(organization: OrganizationData, fullText: false): string {
        const like = this.getOrganizationLike(organization)
        return this.likeChip(organization, like?.value ?? 'none', fullText)
    }

    likeChip(organization: OrganizationData, value: boolean | string, fullText: false): string {
        const stringValue = value?.toString() || 'none'
        const color = this.$getColor(`voirie.opportunities.like.${stringValue}`)
        const tooltip = `${organization.name} [${this.toTranslate('voirie.opportunities.like', stringValue)}]`

        let content: string = this.toTranslate('voirie.opportunities.like', stringValue)
        if (!fullText) {
            switch (stringValue) {
                case 'none':
                    content = '<v-icon icon="mdi-help" size="small" />'
                    break
                case 'true':
                    content = '<v-icon icon="mdi-check" size="small" />'
                    break
                case 'false':
                    content = '<v-icon icon="mdi-close-thick" size="small" />'
                    break
            }
        }

        return `<v-chip variant="${this.$config.public.chips_variant || 'tonal'}" size="small" color="${color}">`
            + `    ${content}`
            + `    <v-tooltip activator="parent" location="bottom">${tooltip}</v-tooltip>`
            + `</v-chip>`
    }

    toTranslate(key, value) {
        return (new TransformerService()).toTranslate(key, value, this.translationName)
    }

    deleteLike(likeData: LikeData): Promise<void> {
        const like = (new Like(likeData)).setOpportunity(this._opportunity.data)
        return like.delete()
    }

    setLike(value): Promise<Like> {
        const like = (new Like()).setOpportunity(this._opportunity.data)
        return like.post({ value })
    }

    setComment(content): Promise<Comment> {
        const comment = (new Comment()).setOpportunity(this._opportunity.data)
        return comment.post({ content })
    }

    deleteComment(commentData: CommentData): Promise<void> {
        const comment = (new Comment(commentData)).setOpportunity(this._opportunity.data)
        return comment.delete()
    }
}
